import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

type FeedbackDetailContextType = {
  detail: FeedbackT | null;
  setDetail: Dispatch<SetStateAction<FeedbackT | null>>;
  updateDetail: (partial: Partial<FeedbackT>) => void;
};

const FeedbackDetailContext = createContext<FeedbackDetailContextType>({
  detail: null,
  setDetail: () => {},
  updateDetail: () => {},
});

const FeedbackDetailProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [detail, setDetail] = useState<FeedbackT | null>(null);

  const updateDetail = useCallback((partial: Partial<FeedbackT>) => {
    setDetail((detail) => {
      if (!detail) return null;
      return { ...detail, ...partial };
    });
  }, []);

  return (
    <FeedbackDetailContext.Provider value={{ detail, setDetail, updateDetail }}>
      {children}
    </FeedbackDetailContext.Provider>
  );
};

const useFeedbackDetailContext = () => {
  const context = useContext(FeedbackDetailContext);
  if (!context) {
    throw new Error(
      'useFeedbackDetailContext must be used within a FeedbackDetailProvider',
    );
  }
  return context;
};

export { FeedbackDetailProvider, useFeedbackDetailContext };
